import * as React from 'react';

import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import type {
  MarketingContent,
  UniversityProgramDeadlines,
} from 'bundles/degree-description/__generated__/baseContentfulTypes';
import EoiInlineModule from 'bundles/degree-description/components/modules/expression-of-interest/EoiInlineModule';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import InfoSectionModule from 'bundles/degree-description/components/modules/info-section/InfoSectionModule';
import InfoListModule from 'bundles/degree-description/components/modules/infolist/InfoListModule';
import InstructorSectionModule from 'bundles/degree-description/components/modules/instructor-section/InstructorSectionModule';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import InlineNavigation from 'bundles/degree-description/components/shared/Navigation/InlineNavigation';
import RichText from 'bundles/degree-description/components/shared/RichText';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';
import { getNavigationItems } from 'bundles/degree-description/utils/utils';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

import _t from 'i18n!nls/degree-description';

const StudentExperiencePageContent = ({
  pageContent,
  eoiContainerText,
  eoiButtons,
  setEoiTrackingIndex,
  personalizedDdpData,
  averageCourseStarRating,
}: PropsFromDegreePageContent) => {
  const {
    studentExperienceAbout,
    backgroundImage,
    partner,
    programName,
    instructorsSection,
    atAGlance,
    studentExperienceValueProp,
    studentExperienceInfoListCollection,
    deadlineSectionCollection,
  } = pageContent;

  const valuePropText = studentExperienceValueProp?.json;
  const valuePropImage = studentExperienceValueProp?.links?.assets?.block?.[0];

  const location = useLocation();
  const { slug } = useParams();
  const routes = useRoutes();

  // Page Section Tracking
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageHeaderTrackingRef = useGetViewPageSectionRef('page_header');
  const pageBodyTrackingRef = useGetViewPageSectionRef('page_body');

  // Personalization
  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partner?.partnerName,
    deadlineSectionCollection?.items as UniversityProgramDeadlines[]
  );

  return (
    <div>
      <SchemaMarkup
        pageName={PAGE_NAMES_MARKUP.studentExperience}
        pathName={location.pathname}
        pageContent={pageContent}
      />
      <HeroModule
        informationalSection={
          <InformationalSectionContainer
            content={
              studentExperienceAbout?.json && (
                <div ref={pageHeaderTrackingRef}>
                  <RichText
                    content={studentExperienceAbout?.json}
                    contentLinks={studentExperienceAbout?.links}
                    linkTrackingName="studentExperienceAbout_body_link"
                    buttonTrackingNameV3="degree_info_link"
                    trackingSection="page_header"
                  />
                </div>
              )
            }
          />
        }
        heroBanner={
          <HeroBanner
            backgroundImageUrl={backgroundImage?.url ?? ''}
            partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
            partnerName={partner?.partnerName ?? ''}
            programName={programName ?? ''}
            averageCourseStarRating={averageCourseStarRating}
            sidebarContent={
              <EoiButtonsBlock
                eoiContainerText={eoiContainerText}
                eoiButtons={eoiButtons}
                trackingIndex={1}
                setEoiTrackingIndex={setEoiTrackingIndex}
              />
            }
          />
        }
        contentInBetween={
          <>
            <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
            <InlineNavigation
              navigationItems={getNavigationItems()}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              parentNavRoute={routes[routes.length - 2].name}
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              currentNavRoute={routes[routes.length - 1].name}
              // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              slug={slug}
            />
          </>
        }
      />

      <div ref={pageBodyTrackingRef}>
        {messagesToDisplay.embeddedMessageToDisplay && (
          <ModuleContainerStyleWrap blueBackground>
            <PersonalizedEmbeddedMsg
              personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
              eoiButtons={eoiButtons}
              setEoiTrackingIndex={setEoiTrackingIndex}
            />
          </ModuleContainerStyleWrap>
        )}

        {studentExperienceInfoListCollection?.items && studentExperienceInfoListCollection.items.length > 0 && (
          <InfoListModule infoListItems={studentExperienceInfoListCollection?.items as MarketingContent[]} />
        )}

        {instructorsSection && (
          <ModuleContainerStyleWrap>
            <InstructorSectionModule
              title={_t('Meet your instructors')}
              json={instructorsSection?.json}
              instructorItems={instructorsSection?.links.entries.block as []}
            />
          </ModuleContainerStyleWrap>
        )}

        {valuePropText && valuePropImage && valuePropImage?.url && valuePropImage?.width && valuePropImage?.height && (
          <ModuleContainerStyleWrap>
            <InfoSectionModule
              description={
                <RichText
                  content={valuePropText}
                  linkTrackingName="studentExperienceValueProp_info_link"
                  buttonTrackingNameV3="degree_info_link"
                  trackingSection="value_proposition"
                />
              }
              media={
                <LazyImgix
                  src={valuePropImage.url}
                  maxWidth={valuePropImage.width}
                  maxHeight={valuePropImage.height}
                  alt={valuePropImage.description || ''}
                  enableFluidWidth={true}
                />
              }
            />
          </ModuleContainerStyleWrap>
        )}

        <EoiInlineModule
          eoiContainerText={eoiContainerText}
          eoiButtons={eoiButtons}
          trackingIndex={2}
          setEoiTrackingIndex={setEoiTrackingIndex}
        />
      </div>
    </div>
  );
};

export default StudentExperiencePageContent;
